import { UzButton, UzCard, UzCheckbox, UzDialog, UzInput } from '@utazzo/ui';
import { PlusCircle, Star, Trash2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import {
  useBankAccounts,
  useCreateBankAccount,
  useRemoveBankAccount,
  useSetDefaultBankAccount,
  useUpdateBankAccount,
} from '../invoices/use-invoices';

import type { BankAccount } from '@utazzo/shared';

/**
 * Company bank accounts manager (multi-bank, mirrors the multi-GST entity
 * manager): many accounts with one default. The default pre-fills new invoices;
 * every account is selectable per invoice and per payment.
 */
export const BankAccountsSettings = ({
  canWrite = true,
}: {
  canWrite?: boolean;
}): React.JSX.Element => {
  const { data: banks, isLoading } = useBankAccounts();
  const setDefault = useSetDefaultBankAccount();
  const remove = useRemoveBankAccount();
  const [dialog, setDialog] = useState<{ bank: BankAccount | null } | null>(null);

  return (
    <UzCard
      // Phones: the card header wraps so "Add bank account" drops under the
      // description instead of squeezing it into a narrow column.
      className="max-sm:[&>*:first-child]:flex-wrap"
      title="Bank accounts"
      description="Company bank accounts shown on invoices and picked when recording payments. One is the default."
      actions={
        canWrite ? (
          <UzButton
            size="sm"
            leftIcon={<PlusCircle className="h-4 w-4" />}
            onClick={() => setDialog({ bank: null })}
          >
            Add bank account
          </UzButton>
        ) : undefined
      }
    >
      {isLoading ? (
        <p className="text-sm text-zinc-500">Loading bank accounts...</p>
      ) : !banks || banks.length === 0 ? (
        <p className="text-sm text-zinc-500">No bank accounts yet. Add one to print on invoices.</p>
      ) : (
        <div className="overflow-x-auto">
          {/* Phones: natural column widths; the wrapper above scrolls sideways. */}
          <table className="w-full text-sm max-sm:w-max max-sm:min-w-full max-sm:whitespace-nowrap">
            <thead>
              <tr className="border-b border-zinc-200 text-left text-xs font-medium text-zinc-500">
                <th className="py-2 pr-4">Label</th>
                <th className="py-2 pr-4">Bank</th>
                <th className="py-2 pr-4">Account no.</th>
                <th className="py-2 pr-4">IFSC</th>
                <th className="py-2 pr-4">Default</th>
                <th className="py-2 pr-4">On invoice</th>
                <th className="py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {banks.map((b) => (
                <tr key={b.id} className="border-b border-zinc-100 last:border-0">
                  <td className="py-2 pr-4 font-medium text-zinc-800">{b.label}</td>
                  <td className="py-2 pr-4">{b.bankName}</td>
                  <td className="py-2 pr-4 tabular-nums">{b.accountNumber}</td>
                  <td className="py-2 pr-4">{b.ifsc ?? '-'}</td>
                  <td className="py-2 pr-4">
                    {b.isDefault ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
                        <Star className="h-3.5 w-3.5 fill-current" />
                        Default
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="py-2 pr-4">{b.showOnInvoice ? 'Yes' : 'No'}</td>
                  <td className="py-2 text-right">
                    {canWrite && (
                      <div className="flex justify-end gap-3">
                        <button
                          type="button"
                          className="text-xs font-medium text-zinc-600 hover:underline"
                          onClick={() => setDialog({ bank: b })}
                        >
                          Edit
                        </button>
                        {!b.isDefault && (
                          <button
                            type="button"
                            className="text-xs font-medium text-(--primary) hover:underline disabled:opacity-50"
                            disabled={setDefault.isPending}
                            onClick={() => setDefault.mutate(b.id)}
                          >
                            Set default
                          </button>
                        )}
                        {!b.isDefault && (
                          <button
                            type="button"
                            aria-label="Remove"
                            className="text-red-600 hover:text-red-700 disabled:opacity-50"
                            disabled={remove.isPending}
                            onClick={() => {
                              if (window.confirm(`Remove bank account "${b.label}"?`))
                                remove.mutate(b.id);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dialog && (
        <BankAccountDialog
          bank={dialog.bank}
          open
          onOpenChange={(open) => !open && setDialog(null)}
        />
      )}
    </UzCard>
  );
};

const BankAccountDialog = ({
  bank,
  open,
  onOpenChange,
}: {
  bank: BankAccount | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.JSX.Element => {
  const create = useCreateBankAccount();
  const update = useUpdateBankAccount();
  const [label, setLabel] = useState('');
  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [ifsc, setIfsc] = useState('');
  const [branchName, setBranchName] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [showOnInvoice, setShowOnInvoice] = useState(true);

  useEffect(() => {
    if (!open) return;
    setLabel(bank?.label ?? '');
    setBankName(bank?.bankName ?? '');
    setAccountNumber(bank?.accountNumber ?? '');
    setIfsc(bank?.ifsc ?? '');
    setBranchName(bank?.branchName ?? '');
    setIsDefault(bank?.isDefault ?? false);
    setShowOnInvoice(bank?.showOnInvoice ?? true);
  }, [open, bank]);

  const canSubmit =
    !!label.trim() &&
    !!bankName.trim() &&
    !!accountNumber.trim() &&
    !create.isPending &&
    !update.isPending;

  const handleSubmit = (): void => {
    if (!canSubmit) return;
    const body = {
      label: label.trim(),
      bankName: bankName.trim(),
      accountNumber: accountNumber.trim(),
      ...(ifsc.trim() ? { ifsc: ifsc.trim() } : {}),
      ...(branchName.trim() ? { branchName: branchName.trim() } : {}),
      isDefault,
      showOnInvoice,
    };
    const done = { onSuccess: () => onOpenChange(false) };
    if (bank) update.mutate({ id: bank.id, body }, done);
    else create.mutate(body, done);
  };

  return (
    <UzDialog
      open={open}
      onOpenChange={onOpenChange}
      title={bank ? 'Edit bank account' : 'Add bank account'}
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </UzButton>
          <UzButton
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={create.isPending || update.isPending}
          >
            {bank ? 'Save' : 'Add'}
          </UzButton>
        </div>
      }
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <UzInput
          label="Label"
          required
          placeholder="e.g. HDFC Current"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
        <UzInput
          label="Bank name"
          required
          value={bankName}
          onChange={(e) => setBankName(e.target.value)}
        />
        <UzInput
          label="Account number"
          required
          className="font-mono"
          value={accountNumber}
          onChange={(e) => setAccountNumber(e.target.value)}
        />
        <UzInput
          label="IFSC"
          className="font-mono"
          value={ifsc}
          onChange={(e) => setIfsc(e.target.value)}
        />
        <UzInput
          label="Branch"
          value={branchName}
          onChange={(e) => setBranchName(e.target.value)}
          containerClassName="sm:col-span-2"
        />
        <UzCheckbox
          label="Default bank"
          description="New invoices default to this bank."
          checked={isDefault}
          onCheckedChange={(c) => setIsDefault(Boolean(c))}
        />
        <UzCheckbox
          label="Show on invoice"
          description="Printed on invoices when no single bank is chosen (the default set, e.g. ICICI + HDFC)."
          checked={showOnInvoice}
          onCheckedChange={(c) => setShowOnInvoice(Boolean(c))}
        />
      </div>
    </UzDialog>
  );
};
